import { css, StyleSheet } from 'aphrodite';
import { Button } from '../../../components-v2/Button';
import { THEME } from '../../../components-v2/Theme/theme';
import { borderOutline, white } from '../../../styles/colors';
import { InfoIcon } from '../../../components/svgs/icons/InfoIcon';
import { SimpleTooltip } from '../../../components/Tooltip';
import { TrashIcon } from '../../../components/svgs/icons/TrashIcon';
import { CANCEL_ON_REPLY_BEHAVIOR_WARNING } from '../../../containers/AutomationFlows/utils/cancelOnReplyConflict';

export interface EditorActionBarProps {
	hasDraft: boolean;
	canPublish: boolean;
	canDiscard: boolean;
	isPublishing: boolean;
	isDiscarding: boolean;
	isActive: boolean;
	showCancelOnReplyConflictWarning?: boolean;
	onPublish: () => void;
	onDiscard: () => void;
	onToggleStatus: () => void;
	onDelete: () => void;
}

export function EditorActionBar({
	hasDraft,
	canPublish,
	canDiscard,
	isPublishing,
	isDiscarding,
	isActive,
	showCancelOnReplyConflictWarning = false,
	onPublish,
	onDiscard,
	onToggleStatus,
	onDelete,
}: EditorActionBarProps) {
	const publishTitle = !hasDraft
		? 'No unpublished changes to publish'
		: canPublish
			? 'Publish changes to make them live'
			: 'Fix validation issues before publishing (click to view details)';

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.leftSection)}>
				{hasDraft && (
					<div className={css(styleSheet.warningBadge)} role='status' aria-label='Unpublished changes'>
						<WarningIcon />
						<span className={css(styleSheet.responsiveLabelLong)} aria-hidden>
							UNPUBLISHED CHANGES
						</span>
						<span className={css(styleSheet.responsiveLabelShort)} aria-hidden>
							UNPUBLISHED
						</span>
					</div>
				)}
				{showCancelOnReplyConflictWarning && (
					<div className={css(styleSheet.conflictBadge)} role='status' aria-label='Cancel on reply conflict'>
						<WarningIcon />
						<span className={css(styleSheet.responsiveLabelLong)} aria-hidden>
							CANCEL ON REPLY CONFLICT
						</span>
						<span className={css(styleSheet.responsiveLabelShort)} aria-hidden>
							CONFLICT
						</span>
						<SimpleTooltip title={CANCEL_ON_REPLY_BEHAVIOR_WARNING} styles={[styleSheet.conflictTooltip]}>
							<Button
								type='button'
								variant='ghost'
								size='icon'
								styles={[styleSheet.infoButton]}
								aria-label='Why is cancel on reply a conflict?'
							>
								<InfoIcon fillColor={THEME.colors.warning.dark} />
							</Button>
						</SimpleTooltip>
					</div>
				)}
			</div>
			<div className={css(styleSheet.rightSection)}>
				<Button
					variant='primary'
					onClick={onPublish}
					disabled={!hasDraft || isPublishing}
					isLoading={isPublishing}
					title={publishTitle}
					aria-label={publishTitle}
				>
					<span className={css(styleSheet.responsiveLabelLong)} aria-hidden>
						Publish Changes
					</span>
					<span className={css(styleSheet.responsiveLabelShort)} aria-hidden>
						Publish
					</span>
				</Button>
				<Button
					variant='secondary'
					onClick={onDiscard}
					disabled={!canDiscard || isDiscarding}
					isLoading={isDiscarding}
					title='Discard draft changes and revert to published version'
					aria-label='Discard draft changes and revert to published version'
				>
					<span className={css(styleSheet.responsiveLabelLong)} aria-hidden>
						Discard Changes
					</span>
					<span className={css(styleSheet.responsiveLabelShort)} aria-hidden>
						Discard
					</span>
				</Button>
				<Button
					variant='secondary'
					onClick={onToggleStatus}
					title={isActive ? 'Disable this automation' : 'Enable this automation'}
					aria-label={isActive ? 'Disable this automation' : 'Enable this automation'}
				>
					{isActive ? (
						<>
							<span className={css(styleSheet.responsiveLabelLong)} aria-hidden>
								Disable Automation
							</span>
							<span className={css(styleSheet.responsiveLabelShort)} aria-hidden>
								Disable
							</span>
						</>
					) : (
						<>
							<span className={css(styleSheet.responsiveLabelLong)} aria-hidden>
								Enable Automation
							</span>
							<span className={css(styleSheet.responsiveLabelShort)} aria-hidden>
								Enable
							</span>
						</>
					)}
				</Button>
				<Button variant='destructive' onClick={onDelete} title='Delete this automation' aria-label='Delete automation'>
					<TrashIcon fillColor={white} />
				</Button>
			</div>
		</div>
	);
}

function WarningIcon() {
	return (
		<svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
			<path
				d='M8 1L15 14H1L8 1Z'
				stroke='currentColor'
				strokeWidth='1.5'
				strokeLinecap='round'
				strokeLinejoin='round'
				fill='none'
			/>
			<path d='M8 6v3' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
			<circle cx='8' cy='11.5' r='0.75' fill='currentColor' />
		</svg>
	);
}

/** Viewports with width at most this value use short labels (below 953px wide). */
const ACTION_BAR_COMPACT_MAX_WIDTH_PX = 953;
const actionBarCompactMaxWidthMedia = `@media (max-width: ${ACTION_BAR_COMPACT_MAX_WIDTH_PX}px)`;

const styleSheet = StyleSheet.create({
	container: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: '12px 16px',
		backgroundColor: white,
		borderRadius: 8,
		border: `1px solid ${borderOutline}`,
		gap: 16,
	},
	leftSection: {
		display: 'flex',
		alignItems: 'center',
		gap: 12,
	},
	rightSection: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
	},
	warningBadge: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		padding: '6px 12px',
		backgroundColor: THEME.colors.danger.light,
		color: THEME.colors.danger.main,
		borderRadius: 16,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.bold,
		letterSpacing: '0.5px',
	},
	conflictBadge: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		padding: '6px 12px',
		backgroundColor: THEME.colors.warning.light,
		color: THEME.colors.warning.dark,
		borderRadius: 16,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.bold,
		letterSpacing: '0.5px',
	},
	infoButton: {
		width: 16,
		height: 16,
		padding: 0,
		border: 'none',
		background: 'none',
		boxShadow: 'none',
		cursor: 'pointer',
		lineHeight: 0,
		':hover': {
			background: 'none',
		},
	},
	conflictTooltip: {
		maxWidth: 320,
		whiteSpace: 'normal',
		wordWrap: 'break-word',
		lineHeight: 1.4,
		fontWeight: THEME.typography.fontWeights.standard,
		letterSpacing: 'normal',
	},
	responsiveLabelLong: {
		display: 'inline',
		[actionBarCompactMaxWidthMedia]: {
			display: 'none',
		},
	},
	responsiveLabelShort: {
		display: 'none',
		[actionBarCompactMaxWidthMedia]: {
			display: 'inline',
		},
	},
});
